'use client';
import React, { useState, useCallback } from 'react';
import { trackCopyClick } from '@/lib/analytics';

interface CopyButtonProps {
  value: string;
  label: string;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  className?: string;
  region?: string;
}

export default function CopyButton({ value, label, variant = 'secondary', disabled = false, className = '', region }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (disabled) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      trackCopyClick(label, region);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = value;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      trackCopyClick(label, region);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [value, disabled, label, region]);

  const base = 'copy-btn inline-flex items-center justify-center gap-1.5 rounded-xl text-sm font-semibold transition-all min-h-[44px] px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
  const variantClass = variant === 'primary' ?'cta-gradient text-white shadow-soft hover:opacity-90' :'bg-white border-2 border-border text-foreground hover:border-primary hover:text-primary';

  const successClass = copied
    ? variant === 'primary' ?'bg-green-600 text-white border-green-600' :'bg-green-50 border-green-400 text-green-700' :'';

  return (
    <button
      onClick={handleCopy}
      disabled={disabled}
      className={`${base} ${variantClass} ${successClass} ${className}`}
      aria-label={copied ? 'Đã sao chép!' : `Sao chép ${label}`}
      title={copied ? 'Đã sao chép!' : `Sao chép ${label}`}
    >
      {copied ? (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>Đã chép!</span>
        </>
      ) : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
          <span>{label}</span>
        </>
      )}
    </button>
  );
}